import {
  EllipsisVerticalIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import { formatDate } from 'date-fns';
import React, { Fragment, useMemo } from 'react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

import { NoNotes as NoNotesIcon } from '@/components/chariot-ui';
import { Dropdown } from '@/components/Dropdown';
import { NoData } from '@/components/legacy-ui/NoData';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Button } from '@/components/ui/button';
import { useRemoveComment } from '@/hooks/useComment';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import { PraetorianAttackerIcon } from '@/sections/attacks/attackPaths/components/icons/PraetorianAttackerIcon';
import { Access, Asset, Risk, Seed, Technology } from '@/types';
import { getAccessEmail } from '@/utils/getAccessEmail';
import {
  isPraetorianEmail,
  maskPraetorianName,
  stripAgentTags,
} from '@/utils/userDisplay.util';

interface Note {
  author?: string;
  date: string;
  message: string;
  idx: number;
}

// eslint-disable-next-line react-refresh/only-export-components
export const named = (
  email: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accesss: any[] = [],
  userType: 'praetorian' | 'customer' | 'sso-customer'
) => {
  // Resolve #access#{uuid} format to actual email
  const resolvedEmail = getAccessEmail(accesss, email);

  // Handle case where resolvedEmail might be undefined
  if (!resolvedEmail) {
    return email;
  }

  // For Praetorian emails, create display name
  let displayName: string | undefined;
  if (resolvedEmail.endsWith('@praetorian.com')) {
    const [fname, lname] = resolvedEmail.split('@')[0].split('.');
    displayName = `${fname} ${lname}`;
  }

  // Use maskPraetorianName to apply proper visibility rules
  const maskedName = maskPraetorianName(resolvedEmail, displayName, userType);

  // Return capitalized span for formatted names, plain text otherwise
  if (displayName && userType === 'praetorian') {
    return <span className="capitalize">{maskedName}</span>;
  }

  return maskedName;
};

// eslint-disable-next-line react-refresh/only-export-components
export const getNotes = (
  resource: Risk | Technology | Asset | Seed,
  removePraetorianAI = false
) => {
  const comments: Note[] = [];

  // Add initial comment if exists (some types like Seed may not have it)
  if ('comment' in resource && resource.comment) {
    comments.push({
      author: resource.username,
      date: resource.created,
      message: resource.comment,
      idx: 0,
    });
  }

  // Add comments from history
  if (resource.history) {
    resource.history.forEach((historyItem, idx) => {
      if (
        historyItem.comment &&
        (removePraetorianAI ? historyItem.by !== 'Praetorian AI' : true)
      ) {
        comments.push({
          author: historyItem.by,
          date: historyItem.updated,
          message: historyItem.comment,
          idx,
        });
      }
    });
  }

  // Sort by date descending
  return comments.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
};

/**
 * Notes Component
 *
 * Displays notes/comments for a resource (Asset, Risk, Technology, Seed).
 * This component always uses an external modal for adding new notes via the
 * onOpenCommentModal callback. The parent component is responsible for
 * rendering the NoteModal.
 *
 * Migration Note (2025-10): The inline NewNoteModal has been removed.
 * All notes are now added via the centralized NoteModal component with
 * mode="markdown" for consistent rich text editing across the platform.
 */
export const Notes: React.FC<{
  resource: Risk | Technology | Asset | Seed;
  resourceType: string;
  /** Handler to open the parent's NoteModal for adding new notes (required) */
  onOpenCommentModal?: () => void;
  /** Optional: Pass access data from parent (skips internal fetch) */
  accesss?: Access[];
  /** Optional: Custom delete handler for resources like Seeds that use different mutations */
  onRemoveNote?: (index: number) => Promise<void>;
}> = ({
  resource,
  resourceType: _resourceType,
  onOpenCommentModal,
  accesss: externalAccesss,
  onRemoveNote,
}) => {
  const { mutateAsync: removeComment } = useRemoveComment();
  const maskingUserType = useMaskingUserType();

  // Fetch access data to resolve #access#{uuid} to actual emails
  // Skip fetch if access data is passed from parent
  const { data: accessGeneric } = useGenericSearch(
    {
      query: '#access',
    },
    { enabled: externalAccesss === undefined }
  );
  const accesss = externalAccesss ?? accessGeneric?.accesss ?? [];

  const handleDeleteComment = async (index: number) => {
    try {
      // Use custom handler if provided (e.g., Seeds use different mutation)
      if (onRemoveNote) {
        await onRemoveNote(index);
        return;
      }
      await removeComment({
        key: resource.key,
        cpe: (resource as Technology)?.cpe,
        remove: index,
      });
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const notes: Note[] = useMemo(() => getNotes(resource, true), [resource]);

  if (notes.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={<NoNotesIcon className="text-chariot-stroke" />}
          title="No Notes Have Been Added"
          description="Add notes to document findings, track decisions, or communicate with your team."
          action={
            onOpenCommentModal ? (
              <Button
                onClick={onOpenCommentModal}
                data-testid="notes-empty-state-add-note-btn"
              >
                <PlusCircleIcon className="size-4" />
                Add Note
              </Button>
            ) : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-4 px-4" data-testid="notes">
      {notes.map((note, index) => {
        const resolvedAuthor = getAccessEmail(accesss, note?.author ?? '');
        const isPraetorianAuthor = isPraetorianEmail(resolvedAuthor || '');
        const showWreathIcon =
          isPraetorianAuthor && maskingUserType !== 'praetorian';

        return (
          <Fragment key={index}>
            <div className={twMerge('note flex min-w-0 gap-4')}>
              {showWreathIcon && (
                <div className="flex shrink-0 items-start pt-1">
                  <PraetorianAttackerIcon className="text-chariot-text-secondary size-8" />
                </div>
              )}
              <div className="flex w-full min-w-0 flex-col gap-1">
                <div className="flex w-full flex-row items-center justify-between">
                  <div className="flex flex-col">
                    <p className="text-lg font-bold">
                      {named(note?.author ?? '', accesss, maskingUserType) ||
                        'Unknown'}
                    </p>
                    <p className="text-chariot-text-secondary text-sm">
                      {formatDate(note.date, 'MMM d, yyyy')}
                    </p>
                  </div>
                  {onOpenCommentModal && (
                    <Dropdown
                      onClick={event => {
                        event.stopPropagation();
                      }}
                      startIcon={
                        <EllipsisVerticalIcon className="m-auto size-6" />
                      }
                      styleType="none"
                      menu={{
                        items: [
                          {
                            label: 'Delete',
                            onClick: () => handleDeleteComment(note.idx),
                          },
                        ],
                      }}
                    />
                  )}
                </div>

                <div className="text-md text-chariot-text-primary mt-4">
                  <MarkdownPreview
                    source={stripAgentTags(note.message, maskingUserType)}
                  />
                </div>
              </div>
            </div>
            {index !== notes.length - 1 && (
              <div className="bg-chariot-stroke h-px w-full" />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};
